import React, { ReactNode } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { DotLegalHeader, DotLegalPaper, DotLegalSwitch, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import GraphSegmentedControl, { GraphSegmentedOption } from "./GraphSegmentedControl";
import EntityGraphHeaderBar from "./EntityGraphHeaderBar";
import EntityGraphInspector from "./EntityGraphInspector";
import { useEntityGraph, useIsTextTruncated, VisibleGraphNode } from "./EntityGraph.hooks";
import { useEntityGraphColors, useEntityGraphStyles } from "./EntityGraph.styles";
import { EntityGraphDepth, EntityGraphEdge, EntityGraphEdgeKind, EntityGraphNode, EntityGraphOverlay } from "./EntityGraph.types";
import { edgeKindTranslationKey, nodeHeight, nodeWidth } from "./EntityGraph.utils";

// Cards and edges that relate to the root only through declared (dashed) sub-processor edges render washed
// out, so the confirmed chain stays visually dominant.
const declaredOnlyOpacity = 0.55;
// While a card is hovered, everything off the hovered->focus paths drops far enough back that the lit paths
// are the only thing the eye follows, yet the rest stays legible enough to orient by.
const hoverDimOpacity = 0.15;

export interface EntityGraphProps {
    nodes: Array<EntityGraphNode>;
    edges: Array<EntityGraphEdge>;
    rootKey: string;
    onRootChange: (key: string) => void;
    overlay?: EntityGraphOverlay;
    toolbarContent?: ReactNode;
    title?: string;
    onClose?: () => void;
}

function EntityGraph(props: EntityGraphProps) {
    const {
        depth,
        onDepthChange,
        showDeclaredOnly,
        onShowDeclaredOnlyChange,
        hasDeclaredOnly,
        transform,
        zoomPercent,
        zoomIn,
        zoomOut,
        fitView,
        canvasRef,
        svgRef,
        isPanning,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        rootNode,
        visibleNodes,
        edgeSegments,
        hoveredEdgeSegment,
        blastRadius,
        dependencies,
        associated,
        onRootChange,
        onNodeHoverChange,
        onEdgeHoverChange,
        overlay,
    } = useEntityGraph(props);
    const styles = useEntityGraphStyles();
    const colors = useEntityGraphColors();
    const { translateString } = useTranslation();

    const depthOptions: Array<GraphSegmentedOption<EntityGraphDepth>> = [
        { label: translateString("entityGraphDepthOneHop"), value: 1 },
        { label: translateString("entityGraphDepthTwoHops"), value: 2 },
        { label: translateString("entityGraphDepthAll"), value: "all" },
    ];

    const edgeKindsLabel = (kinds: Array<EntityGraphEdgeKind>) => kinds.map((kind) => translateString(edgeKindTranslationKey(kind))).join(" · ");

    return (
        <Box sx={styles.container}>
            <EntityGraphHeaderBar title={props.title} onClose={props.onClose} />
            <Box sx={styles.body}>
                <Box ref={canvasRef} sx={[styles.canvasWrap, ...(isPanning ? [styles.canvasWrapPanning] : [])]}>
                    <Box sx={styles.canvasFilterBar}>
                        <DotLegalPaper sx={{ paddingY: 1 }}>
                            <Box sx={styles.canvasFilterBarContent}>
                                {props.toolbarContent}
                                <Box sx={styles.toolbarGroup}>
                                    <DotLegalHeader headerStyle="medium" marginBottom={0}>
                                        {translateString("entityGraphDepth")}:
                                    </DotLegalHeader>
                                    <GraphSegmentedControl options={depthOptions} selected={depth} onSelect={onDepthChange} />
                                </Box>
                                {hasDeclaredOnly && (
                                    <DotLegalSwitch
                                        checked={showDeclaredOnly}
                                        onChange={onShowDeclaredOnlyChange}
                                        rightLabel={translateString("entityGraphShowDeclaredOnly")}
                                    />
                                )}
                            </Box>
                        </DotLegalPaper>
                    </Box>
                    <Box
                        component="svg"
                        ref={svgRef}
                        sx={styles.svg}
                        role="img"
                        aria-label={translateString("entityGraphTitle")}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        <defs>
                            <marker id="entity-graph-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                                <path d="M0,0 L10,5 L0,10 z" fill={colors.edge} />
                            </marker>
                            <marker
                                id="entity-graph-arrow-up"
                                viewBox="0 0 10 10"
                                refX="9"
                                refY="5"
                                markerWidth="7"
                                markerHeight="7"
                                orient="auto-start-reverse"
                            >
                                <path d="M0,0 L10,5 L0,10 z" fill={colors.upEdge} />
                            </marker>
                            <marker
                                id="entity-graph-arrow-hover"
                                viewBox="0 0 10 10"
                                refX="9"
                                refY="5"
                                markerWidth="7"
                                markerHeight="7"
                                orient="auto-start-reverse"
                            >
                                <path d="M0,0 L10,5 L0,10 z" fill={colors.edgeHover} />
                            </marker>
                            <marker
                                id="entity-graph-arrow-thread"
                                viewBox="0 0 10 10"
                                refX="9"
                                refY="5"
                                markerWidth="7"
                                markerHeight="7"
                                orient="auto-start-reverse"
                            >
                                <path d="M0,0 L10,5 L0,10 z" fill={colors.rootStroke} />
                            </marker>
                        </defs>
                        <g transform={`translate(${transform.tx},${transform.ty}) scale(${transform.scale})`}>
                            <g>
                                {edgeSegments.map((segment) => (
                                    <DotLegalTooltip key={segment.key} text={edgeKindsLabel(segment.kinds)} followCursor>
                                        <g
                                            className={segment.undirected ? "entity-graph-edge entity-graph-edge-undirected" : "entity-graph-edge"}
                                            opacity={segment.dimmed ? hoverDimOpacity : segment.faded ? declaredOnlyOpacity : undefined}
                                            onMouseEnter={() => onEdgeHoverChange(segment.key)}
                                            onMouseLeave={() => onEdgeHoverChange(null)}
                                        >
                                            <path
                                                className="entity-graph-edge-line"
                                                d={segment.path}
                                                fill="none"
                                                stroke={segment.highlighted ? colors.rootStroke : segment.up ? colors.upEdge : colors.edge}
                                                strokeWidth={segment.highlighted ? 2.5 : 1.5}
                                                strokeDasharray={segment.dashed ? "5 4" : undefined}
                                                opacity={segment.up && !segment.highlighted ? 0.55 : 1}
                                                markerEnd={
                                                    segment.undirected
                                                        ? undefined
                                                        : segment.highlighted
                                                          ? "url(#entity-graph-arrow-thread)"
                                                          : segment.up
                                                            ? "url(#entity-graph-arrow-up)"
                                                            : "url(#entity-graph-arrow)"
                                                }
                                            />
                                            {/* Invisible wide twin of the line so hovering doesn't require pixel-perfect aim. */}
                                            <path d={segment.path} fill="none" stroke="transparent" strokeWidth={12} pointerEvents="stroke" />
                                        </g>
                                    </DotLegalTooltip>
                                ))}
                                {/* SVG paints in document order, so the hovered line is re-drawn last to sit above
                                    every other line — the CSS :hover recolors the original in place, and this
                                    pointer-transparent twin lifts it clear of crossings. */}
                                {hoveredEdgeSegment && (
                                    <path
                                        d={hoveredEdgeSegment.path}
                                        fill="none"
                                        stroke={colors.edgeHover}
                                        strokeWidth={2.5}
                                        strokeDasharray={hoveredEdgeSegment.dashed ? "5 4" : undefined}
                                        markerEnd={hoveredEdgeSegment.undirected ? undefined : "url(#entity-graph-arrow-hover)"}
                                        pointerEvents="none"
                                    />
                                )}
                            </g>
                            <g>
                                {visibleNodes.map((visibleNode) => (
                                    <GraphNodeCard
                                        key={visibleNode.node.key}
                                        visibleNode={visibleNode}
                                        onRootChange={onRootChange}
                                        onHoverChange={onNodeHoverChange}
                                        colors={colors}
                                    />
                                ))}
                            </g>
                        </g>
                    </Box>
                    <Box sx={styles.zoomControls}>
                        <DotLegalTooltip text={translateString("entityGraphZoomIn")} position="left">
                            <IconButton sx={styles.zoomButton} onClick={zoomIn}>
                                +
                            </IconButton>
                        </DotLegalTooltip>
                        <DotLegalTooltip text={translateString("entityGraphZoomOut")} position="left">
                            <IconButton sx={styles.zoomButton} onClick={zoomOut}>
                                −
                            </IconButton>
                        </DotLegalTooltip>
                        <DotLegalTooltip text={translateString("entityGraphFitToView")} position="left">
                            <IconButton sx={styles.zoomButton} onClick={fitView}>
                                ⤢
                            </IconButton>
                        </DotLegalTooltip>
                        <Box sx={styles.zoomLabel}>{zoomPercent}%</Box>
                    </Box>
                </Box>
                <EntityGraphInspector
                    rootNode={rootNode}
                    blastRadius={blastRadius}
                    dependencies={dependencies}
                    associated={associated}
                    onRootChange={onRootChange}
                    overlay={overlay}
                />
            </Box>
        </Box>
    );
}

interface GraphNodeCardProps {
    visibleNode: VisibleGraphNode;
    onRootChange: (key: string) => void;
    onHoverChange: (key: string | null) => void;
    colors: ReturnType<typeof useEntityGraphColors>;
}

function GraphNodeCard({ visibleNode, onRootChange, onHoverChange, colors }: GraphNodeCardProps) {
    const { node, position, isRoot, isDeclaredOnly, dimmed, decoration } = visibleNode;
    const { ref: nameRef, isTruncated: isNameTruncated } = useIsTextTruncated(node.label);
    const badgeWidth = decoration?.badgeText ? 22 + (decoration.badgeText.length - 2) * 7 : 0;

    return (
        <g
            className="entity-graph-node"
            opacity={dimmed ? hoverDimOpacity : isDeclaredOnly ? declaredOnlyOpacity : undefined}
            transform={`translate(${position.x},${position.y})`}
            tabIndex={0}
            role="button"
            aria-label={`${node.label}, ${node.typeLabel}`}
            onClick={() => onRootChange(node.key)}
            onMouseEnter={() => onHoverChange(node.key)}
            onMouseLeave={() => onHoverChange(null)}
            onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onRootChange(node.key);
                }
            }}
            onPointerDown={(event: React.PointerEvent) => {
                event.stopPropagation();
                // Keep mouse clicks from focusing the group — otherwise the browser paints its default black
                // focus ring around the card. Keyboard focus (tab) is unaffected and styled via :focus-visible.
                event.preventDefault();
            }}
        >
            <rect
                width={nodeWidth}
                height={nodeHeight}
                rx={9}
                fill={decoration?.fill ?? colors.nodeFill}
                stroke={isRoot ? colors.rootStroke : (decoration?.stroke ?? colors.nodeStroke)}
                strokeWidth={isRoot ? 2.75 : 1.25}
            />
            <foreignObject x={0} y={0} width={nodeWidth} height={nodeHeight}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: "2px",
                        padding: "4px 10px",
                        boxSizing: "border-box",
                    }}
                >
                    <DotLegalTooltip text={isNameTruncated ? node.label : ""}>
                        <Typography ref={nameRef} noWrap sx={{ fontSize: "12.5px", fontWeight: 600, lineHeight: 1.3, color: colors.nodeText }}>
                            {node.label}
                        </Typography>
                    </DotLegalTooltip>
                    <Typography
                        noWrap
                        sx={{ fontSize: "9px", letterSpacing: "0.11em", textTransform: "uppercase", lineHeight: 1.2, color: colors.nodeTypeText }}
                    >
                        {node.typeLabel}
                    </Typography>
                    {decoration?.chip && <Box sx={{ display: "flex" }}>{decoration.chip}</Box>}
                </Box>
            </foreignObject>
            {decoration?.badgeText && (
                <g>
                    {decoration.badgeTooltip && <title>{decoration.badgeTooltip}</title>}
                    <rect
                        x={nodeWidth - badgeWidth + 6}
                        y={-9}
                        width={badgeWidth}
                        height={19}
                        rx={9.5}
                        fill={decoration.stroke ?? colors.nodeStroke}
                        stroke="#fff"
                        strokeWidth={1.5}
                    />
                    <text x={nodeWidth - badgeWidth / 2 + 6} y={4} fontSize={10} fontWeight={600} fill={colors.badgeText} textAnchor="middle">
                        {decoration.badgeText}
                    </text>
                </g>
            )}
            {node.leafChips.length > 0 && (
                <g>
                    <title>{node.leafChips.join("\n")}</title>
                    <rect x={nodeWidth - 52} y={nodeHeight - 9} width={46} height={18} rx={9} fill={colors.chipFill} stroke={colors.chipStroke} />
                    <text x={nodeWidth - 29} y={nodeHeight + 4} fontSize={9} fontWeight={600} fill={colors.chipText} textAnchor="middle">
                        🔗 {node.leafChips.length}
                    </text>
                </g>
            )}
        </g>
    );
}

export default EntityGraph;
